<script setup lang="ts">
import { ref, computed, onMounted } from "vue";
import type { PnMultiselectOption } from "@postnord/web-components/types";
import { storeToRefs } from "pinia";
import { userStickers } from "@/stores/stickers";
import { useTagManagementStore } from "@/stores/tagManagement";
import type { ApiTagCategory } from "@/types/tagManagement";
import constant from "@/constant";

const stickerStore = userStickers();
const { createCollectionReqestParams } = storeToRefs(stickerStore);
const tagManagementStore = useTagManagementStore();

const dropdownSelections = ref<Record<string, PnMultiselectOption[]>>({});
const isLoading = ref(false);
const validated = ref(false);

const visualCategoryGroups = computed<ApiTagCategory[]>(() =>
  tagManagementStore
    .getThemeCategories(constant.TAG_THEME_IDS.VISUAL_QUALITY)
    .filter(
      (category) =>
        !category.visibleFlags ||
        category.visibleFlags.isStickerCollection !== false,
    ),
);

const getEnglishLabel = (group: ApiTagCategory): string =>
  `${group.enCategoryName} (select at least one)`;

const isGroupInvalid = (groupId: number): boolean => {
  if (!validated.value) return false;
  const selections =
    createCollectionReqestParams.value.visualQualitySelections?.[groupId] ?? [];
  return selections.length === 0;
};

const getGroupError = (groupId: number): string =>
  isGroupInvalid(groupId)
    ? constant.STICKER_VALIDATION.SELECT_AT_LEAST_ONE_OPTION
    : "";

const validate = (): boolean => {
  validated.value = true;
  return visualCategoryGroups.value.every((group) => {
    const selections =
      createCollectionReqestParams.value.visualQualitySelections?.[
        group.categoryId
      ] ?? [];
    return selections.length > 0;
  });
};

defineExpose({ validate });

const getOptions = (group: ApiTagCategory): PnMultiselectOption[] =>
  dropdownSelections.value[group.categoryId] ?? [];

const onSelectionChange = (
  groupId: number,
  event: CustomEvent<PnMultiselectOption[]>,
) => {
  dropdownSelections.value = {
    ...dropdownSelections.value,
    [groupId]: [...event.detail],
  };
  const checkedIds = event.detail
    .filter((option) => option.checked)
    .map((option) => String(option.value));
  stickerStore.setVisualQualitySelections({
    ...(createCollectionReqestParams.value.visualQualitySelections ?? {}),
    [groupId]: checkedIds,
  });
  stickerStore.syncCategoriesFromSelections();
};

onMounted(async () => {
  isLoading.value = true;
  if (!tagManagementStore.tagTree) {
    await tagManagementStore.getTagTree();
  }
  const groups = tagManagementStore.getThemeCategories(
    constant.TAG_THEME_IDS.VISUAL_QUALITY,
  );
  const storedSelections =
    createCollectionReqestParams.value.visualQualitySelections ?? {};
  const initialSelections: Record<string, PnMultiselectOption[]> = {};
  groups.forEach((group) => {
    const storedGroupIds = storedSelections[group.categoryId] ?? [];
    initialSelections[group.categoryId] = (group.children ?? []).map(
      (child) => ({
        label: child.svCategoryName,
        value: String(child.categoryId),
        checked: storedGroupIds.includes(String(child.categoryId)),
      }),
    );
  });
  dropdownSelections.value = initialSelections;
  isLoading.value = false;
});
</script>

<template>
  <section>
    <div class="visual-quality">
      <h3 class="pn-m">What is the visual quality of these stickers?</h3>
      <p class="pn-s width-67">
        Try to describe how these stickers look by selecting from the three
        categories below.
      </p>
      <div class="sticker-list">
        <div
          class="sticker-item"
          v-for="sticker in createCollectionReqestParams.stickers"
          :key="sticker.filename"
        >
          <img
            class="thumb"
            :src="`data:image/svg+xml;base64,${sticker.base64}`"
            :alt="sticker.filename"
          />
        </div>
      </div>
      <div :class="[{ spinner: isLoading }]">
        <pn-spinner size="3" v-show="isLoading" />
      </div>
      <div v-if="!isLoading" class="dropdowns">
        <pn-multiselect
          v-for="group in visualCategoryGroups"
          :key="group.categoryId"
          :label="getEnglishLabel(group)"
          :options="getOptions(group)"
          :invalid="isGroupInvalid(group.categoryId)"
          :error="getGroupError(group.categoryId)"
          @allOptions="onSelectionChange(group.categoryId, $event)"
        ></pn-multiselect>
      </div>
    </div>
  </section>
</template>

<style lang="scss" scoped>
.visual-quality {
  display: flex;
  flex-direction: column;
  padding-left: 32px;
}
.width-67 {
  width: 67%;
}
.sticker-list {
  display: grid;
  grid-template-columns: repeat(13, 1fr);
  gap: 10px;
  padding: 8px 0;
}
.sticker-item {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 4px;
  height: 60px;
}
.thumb {
  width: 100%;
  height: 60px;
  object-fit: contain;
}
.dropdowns {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(0, 1fr));
  gap: 16px;
  padding-top: 15px;
}
</style>
